/**
 * Web-standard base64 codec (P6/PRNT-4559): replaces browser-bundle Buffer usage
 * ahead of the webpack-5 switch (wp5 ships no Buffer shim). Works identically
 * under SSR (Node >=16 has atob/btoa/TextEncoder globals) and browsers.
 *
 * Decoder is TOLERANT to match Buffer.from(x, 'base64') semantics that existing
 * data in the wild depends on: base64url characters ('-','_'), missing padding,
 * and ASCII whitespace are accepted (whitespace matters because a legacy
 * unencoded '+' in a query param URL-decodes to a space). Do not make it strict
 * without auditing stored payloads.
 *
 * Documented tolerance bound vs Buffer: mid-string '=' (Buffer truncates at it;
 * this codec throws) and non-whitespace invalid characters (Buffer skips them;
 * this codec throws) remain OUT of tolerance. Extend the parity spec's dirty
 * corpus first if a real payload ever needs either.
 */

const CHUNK = 0x8000; // fromCharCode arg-count safety

export const encodeBytesToBase64 = (bytes: Uint8Array): string => {
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
};

const normalizeBase64 = (base64: string): string => {
  // Strip ASCII whitespace BEFORE the pad computation (Buffer parity; WHATWG
  // forgiving-base64 does the same). Counting whitespace into the length would
  // mis-pad and make atob throw on input bare atob itself accepts.
  const mapped = base64.replace(/\s+/g, '').replace(/-/g, '+').replace(/_/g, '/');
  const padLength = (4 - (mapped.length % 4)) % 4;
  return mapped + '='.repeat(padLength);
};

export const decodeBase64ToBytes = (base64: string): Uint8Array => {
  const binary = atob(normalizeBase64(base64));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
};

export const encodeStringToBase64 = (s: string): string => encodeBytesToBase64(new TextEncoder().encode(s));

export const decodeBase64ToString = (base64: string): string => new TextDecoder().decode(decodeBase64ToBytes(base64));
